import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type DrawDefinition, EntryStatusEnum, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import NiceModal from '@ebay/nice-modal-react';
import { IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { type Column, type Row } from '@tanstack/react-table';
import { deepEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom, useResetAtom } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import lazyWithPreload from 'react-lazy-with-preload';
import { useParams } from 'react-router-dom';
import { drawDefinitionConstants, tournamentEngine } from 'tods-competition-factory';

import HelpIcon from 'assets/icons/icn _ 24px _ help.svg?react';
import { eventAtomFamily, qualifyingDrawGeneratedAtomFamily, qualifyingDrawParticipantsAtomFamily } from 'atoms/events';
import { tournamentAtom } from 'atoms/tournament';
import LinkButton from 'components/buttons/LinkButton';
import StandardButton from 'components/buttons/StandardButton';
import CompetitorsTableEmptyState from 'components/tables/competitors/CompetitorsTableEmptyState';
import CompetitorsTableTitle from 'components/tables/competitors/CompetitorsTableTitle';
import { qualifyingRequiredColumns, qualifyingRequiredTeamColumns } from 'components/tables/competitors/config';
import { useStyles } from 'components/tables/competitors/qualifying/styles';
import { CompetitorsColumns } from 'components/tables/competitors/types';
import { useCompetitorsTableColumns } from 'components/tables/competitors/useCompetitorsTableColumns';
import { getOrderedParticipants } from 'components/tables/competitors/utils';
import DrawParticipantsTable from 'components/tables/eventsSplitDraws/drawParticipantsTable/DrawParticipantsTable';
import TMXTooltip from 'components/tooltip/TMXTooltip';
import { BodyLargeBold, BodyLargeLight, BodyRegular } from 'components/typography/Typography';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { useCompetitorsPopover } from 'containers/eventsSplitDraws/entries/EntriesActions';
import SeedingButton from 'containers/seeding/SeedingButton';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import useSeeds from 'hooks/tournament/useSeeds';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import QualifyingBulkActions from './QualfyingBulkActions';

const { DOUBLES, TEAM } = EventTypeEnum;
const { QUALIFYING } = drawDefinitionConstants;

const SetPlayerStatusDialog = lazyWithPreload(() => import('../../eventsSplitDraws/splitDraws/SetPlayerStatusDialog'));

interface QualifyingParticipantsTableProps {
  drawDefinition: DrawDefinition;
  qualifyingDrawParticipants: ParticipantInterface[];
  onUnpairPlayers?: (rowItemId: string, participants: ParticipantInterface[]) => void;
  setEntryPositionsState: (entryPositions: { participantId: string; entryPosition: number }[]) => void;
  columnsSelection: Array<string>;
  saveColumnsSelection: (columns: Column<ParticipantInterface>[]) => void;
}

const QualifyingParticipantsTable: React.FC<QualifyingParticipantsTableProps> = ({
  drawDefinition,
  qualifyingDrawParticipants,
  onUnpairPlayers,
  setEntryPositionsState,
  columnsSelection,
  saveColumnsSelection,
}) => {
  useEffect(() => {
    SetPlayerStatusDialog.preload();
  }, []);
  const { classes } = useStyles();
  const [toggleQualifying] = useMutateTournament('toggleQualifying');
  const [setPlayerStatus] = useMutateTournament('setPlayerStatus');
  const [setEntryPositions, setEntryPositionsProgress] = useMutateTournament('setEntryPositions');
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const [isEditReorder, setIsEditReorder] = useState<boolean>(false);
  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const { t } = useTranslation();
  const openCompetitorsPopover = useCompetitorsPopover();
  const showProfileDialog = usePlayersProfileDialog();
  const resetQualifyingParticipantsAtom = useResetAtom(qualifyingDrawParticipantsAtomFamily(eventId));

  const isDouble = event?.eventType === DOUBLES;
  const isTeam = event?.eventType === TEAM;
  const isEmpty = qualifyingDrawParticipants?.length === 0;
  const isQualifyingDrawGenerated = useAtomValue(qualifyingDrawGeneratedAtomFamily(eventId));

  const structure =
    drawDefinition?.structures?.length &&
    drawDefinition.structures?.find((structure) => structure?.stage === QUALIFYING);

  const getSeedAssignments = useCallback(
    () => tournamentEngine.getStructureSeedAssignments({ structure })?.seedAssignments,
    [structure],
  );

  const seedAssignments = useAtomValue(selectAtom(tournamentAtom, getSeedAssignments, deepEqual));

  const {
    inputRefs: seedRefs,
    isLoading,
    isSeedingPublished,
    seeds,
    clear: clearSeeds,
    publish: publishSeeds,
    save: saveSeeds,
    unpublish: unpublishSeeds,
    update: updateSeed,
  } = useSeeds({
    event,
    entries: qualifyingDrawParticipants,
    isQualifying: true,
    seedAssignments,
  });

  const handleSaveReorder = useCallback(async () => {
    const orderedQualifyingParticipants = getOrderedParticipants(qualifyingDrawParticipants);
    await setEntryPositions({
      drawId: drawDefinition?.drawId,
      eventId,
      entryPositions: orderedQualifyingParticipants,
      entryStatus: QUALIFYING,
    });
    resetQualifyingParticipantsAtom();
    setIsEditReorder(false);
  }, [qualifyingDrawParticipants, setEntryPositions, drawDefinition?.drawId, eventId, resetQualifyingParticipantsAtom]);

  const handleChangeEditMode = useCallback(() => {
    setIsEditReorder(!isEditReorder);
  }, [isEditReorder]);

  const handleSetPlayerStatusSave = useCallback(
    async (playerStatus: string, entriesIds: string[]) => {
      await setPlayerStatus?.(entriesIds, event.eventId, playerStatus);
    },
    [event.eventId, setPlayerStatus],
  );

  const handleOpenSetPlayerStatusDialog = useCallback(
    async (participantIds: string[], resetRowSelection: () => void) => {
      await NiceModal.show(SetPlayerStatusDialog, {
        selectedEntriesIds: participantIds,
        onSave: async (playerStatus: string, selectedEntriesIds: string[]) => {
          await handleSetPlayerStatusSave(playerStatus, selectedEntriesIds);
          resetRowSelection();
        },
      });
    },
    [handleSetPlayerStatusSave],
  );

  const handleRemoveQualifying = useCallback(
    () => toggleQualifying(event.eventId, false),
    [event.eventId, toggleQualifying],
  );

  const handleChangeIsEditMode = useCallback((editMode: boolean) => setIsInEditMode(editMode), []);

  const handleUnpairPlayers = useCallback(
    (participantId: string) => onUnpairPlayers(participantId, qualifyingDrawParticipants),
    [qualifyingDrawParticipants, onUnpairPlayers],
  );
  const renderQualifyingBulkActions = useCallback(
    (selectedFlatRows: Row<ParticipantInterface>[], onResetRowSelection: () => void) => (
      <QualifyingBulkActions
        selectedFlatRows={selectedFlatRows}
        onClickSetPlayerStatus={handleOpenSetPlayerStatusDialog}
        onResetRowSelection={onResetRowSelection}
      />
    ),
    [handleOpenSetPlayerStatusDialog],
  );
  const handleOpenCompetitorsPopover = useCallback(
    (participant: ParticipantInterface, clickEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      openCompetitorsPopover(participant, { onUnpairPlayers: handleUnpairPlayers }, clickEvent.currentTarget, event),
    [openCompetitorsPopover, handleUnpairPlayers, event],
  );

  const requiredColumns = useMemo(() => {
    const columns = isTeam ? qualifyingRequiredTeamColumns : qualifyingRequiredColumns;
    return isQualifyingDrawGenerated ? columns.filter((column) => column !== CompetitorsColumns.SELECTION) : columns;
  }, [isQualifyingDrawGenerated, isTeam]);

  const qualifyingTableColumns = useCompetitorsTableColumns({
    classes: '',
    event,
    isEditReorder,
    isInEditMode,
    isTeamTable: isTeam,
    manualSeedRefs: seedRefs,
    onChangeSeeding: updateSeed,
    onOpenPlayerProfile: showProfileDialog,
    onClickAction: handleOpenCompetitorsPopover,
    requiredColumns,
    seeds,
  });

  const additionalButtons = useMemo(
    () => (
      <Grid container columnSpacing={2} direction="row" flexWrap="nowrap">
        <Grid item>
          <SeedingButton
            entries={qualifyingDrawParticipants}
            isInEditMode={isInEditMode}
            isSeedingPublished={isSeedingPublished}
            loading={isLoading}
            seedingActions={{
              onClearSeeds: clearSeeds,
              onPublishSeeds: publishSeeds,
              onSaveSeeds: saveSeeds,
              onUnpublishSeeds: unpublishSeeds,
            }}
            onSetIsInEditMode={handleChangeIsEditMode}
          />
        </Grid>
        {!isEmpty && !isQualifyingDrawGenerated && (
          <Grid item minWidth="max-content">
            <StandardButton
              loading={setEntryPositionsProgress}
              variant="primary"
              onClick={isEditReorder ? handleSaveReorder : handleChangeEditMode}
            >
              {isEditReorder ? t('save') : t('editOrder')}
            </StandardButton>
          </Grid>
        )}
      </Grid>
    ),
    [
      qualifyingDrawParticipants,
      t,
      isEmpty,
      isInEditMode,
      isSeedingPublished,
      isLoading,
      clearSeeds,
      publishSeeds,
      saveSeeds,
      unpublishSeeds,
      handleChangeIsEditMode,
      isQualifyingDrawGenerated,
      setEntryPositionsProgress,
      isEditReorder,
      handleSaveReorder,
      handleChangeEditMode,
    ],
  );

  return (
    <>
      <Grid container alignItems="center" direction="row" gap={1}>
        <Grid item>
          <CompetitorsTableTitle
            length={`${qualifyingDrawParticipants?.length}`}
            text={isTeam ? t('qualifyingTeams') : t('qualifyingPlayers')}
          />
        </Grid>
        <Grid item className={classes.removeLinkWrapper}>
          <LinkButton
            disabled={qualifyingDrawParticipants?.length > 0}
            variant="secondary"
            onClick={handleRemoveQualifying}
          >
            {t('remove')}
          </LinkButton>
          {!isEmpty && (
            <TMXTooltip
              arrow
              open={tooltipOpen}
              placement="left-start"
              title={<BodyRegular>{t('removeQualifyingInfo')}</BodyRegular>}
              onClose={() => setTooltipOpen(false)}
            >
              <IconButton disableFocusRipple disableRipple size="small" onClick={() => setTooltipOpen(true)}>
                <HelpIcon />
              </IconButton>
            </TMXTooltip>
          )}
        </Grid>
      </Grid>
      {!isEmpty ? (
        <DrawParticipantsTable
          additionalButtons={additionalButtons}
          columns={qualifyingTableColumns}
          data={qualifyingDrawParticipants}
          isDouble={isDouble}
          isEditReorder={isEditReorder}
          isEnableMultiRowSelection={!isQualifyingDrawGenerated}
          saveColumnsSelection={saveColumnsSelection}
          savedColumns={columnsSelection}
          selectedActions={renderQualifyingBulkActions}
          setEntryPositionsState={setEntryPositionsState}
          type={EntryStatusEnum.QUALIFIER}
        />
      ) : (
        <CompetitorsTableEmptyState>
          <BodyLargeBold>{t('noQualifyingPlayers')}</BodyLargeBold>
          <BodyLargeLight>{t('noQualifyingPlayersMessage')}</BodyLargeLight>
        </CompetitorsTableEmptyState>
      )}
    </>
  );
};

export default QualifyingParticipantsTable;
